/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useMemo, useCallback, useEffect, JSX } from "react";
import { Box, Typography, TextField, Select, MenuItem, CircularProgress, InputAdornment, Autocomplete, IconButton, Tooltip, Checkbox, FormControlLabel, Button } from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import AddIcon from "@mui/icons-material/Add";
import debounce from "lodash/debounce";
import { DroppableProvided } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import PictureCard from "../Other/PictureCard";
import AlbumSongsPopup from "../Other/AlbumSongsPopup";
import CustomItemPopup, { listTypeToCustomType } from "../Other/CustomItemPopup";

const sortYears = (years: string[]) =>
  years.sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (Number.isNaN(numA) && Number.isNaN(numB)) return a.localeCompare(b);
    if (Number.isNaN(numA)) return 1;
    if (Number.isNaN(numB)) return -1;
    return numB - numA;
  });

type AlbumItem = {
  Id: number;
  Artist?: string;
  AlbumName?: string;
  SongName?: string;
  AlbumId?: number;
  ArtistId?: number;
  YearReleased?: number;
  [key: string]: any;
};

type PaginationInfo = {
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
} | null;

type AlbumLayoutProps = {
  provided: DroppableProvided;
  albums: AlbumItem[];
  artists: AlbumItem[];
  songs?: AlbumItem[];
  covers: Record<string, Record<number, string>>;
  coverType: string;
  renderedItems?: AlbumItem[];
  state: {
    listType: string;
    initialGroupByType?: string;
    initialGroupByValue?: string;
    defaultYear?: string;
    isAuthenticated?: boolean;
    customItems?: Record<string, AlbumItem[]>;
    [key: string]: any;
  };
  getCommands: { getCommon: (command: string) => Promise<any> };
  createCommands?: any;
  postCommands?: any;
  mainFunctions?: any;
  updateUrlParams?: (listType?: string, groupValue?: string) => void;
  basicFunctions?: { updateState: (state: any) => void; [key: string]: any };
  onArtistFilterChange?: (artist: string | null) => void;
  onYearChange?: (year: string) => void;
  onLoadMore?: () => void;
  onShuffle?: () => void;
  pagination?: PaginationInfo;
};

const AlbumLayout = (props: AlbumLayoutProps) => {
  const { provided, albums, artists, songs = [], covers, coverType, renderedItems = [] } = props;
  const { listType } = props.state;
  const { t } = useTranslation();

  const initialGroupByType = props.state?.initialGroupByType;
  const initialGroupByValue = props.state?.initialGroupByValue;
  const defaultYear = props.state?.defaultYear || "2025";
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(listType === "Year" ? defaultYear : "All");
  const [visibleCount, setVisibleCount] = useState(48);
  const [searchResults, setSearchResults] = useState<AlbumItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [customPopupOpen, setCustomPopupOpen] = useState(false);
  const [customPopupName, setCustomPopupName] = useState("");

  // Available years for year mode dropdown
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [yearCounts, setYearCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (listType === "Year") {
      props.getCommands.getCommon("getAlbumYears").then((data: any) => {
        if (Array.isArray(data)) {
          setAvailableYears(data.map((d: any) => String(d.YearReleased)));
          setYearCounts(Object.fromEntries(data.map((d: any) => [String(d.YearReleased), d.count])));
        }
      }).catch(() => {});
    }
  }, [listType, props.getCommands]);

  // Artist autocomplete state
  const [artistSearchInput, setArtistSearchInput] = useState("");
  const [artistSuggestions, setArtistSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Album songs popup state
  const [songPopup, setSongPopup] = useState<{
    open: boolean;
    albumId: number;
    albumName: string;
    artistName: string;
    albumCover?: string;
  }>({ open: false, albumId: 0, albumName: "", artistName: "" });

  const handleSongPreview = useCallback((item: AlbumItem, cover?: string) => {
    setSongPopup({
      open: true,
      albumId: item.Id,
      albumName: item.AlbumName || "",
      artistName: item.Artist || "",
      albumCover: cover,
    });
  }, []);

  const isArtistMode = listType === "Artists";
  const isYearMode = listType === "Year";
  const isAlbumMode = listType === "Albums";
  const isSongMode = listType === "Songs";

  const labelKey = isArtistMode ? "Artist" : isSongMode ? "SongName" : "AlbumName";
  const unknownArtistLabel = t("tierList.unknownArtist", { defaultValue: "Unknown Artist" });
  const unknownYearLabel = t("tierList.unknownYear", { defaultValue: "Unknown" });

  const listItems = useMemo(() => {
    if (isArtistMode) {
      return artists;
    }
    if (isSongMode) {
      return songs;
    }
    return albums;
  }, [albums, artists, songs, isArtistMode, isSongMode]);

  const renderedItemIds = useMemo(
    () => new Set(renderedItems.map((item) => item.Id)),
    [renderedItems]
  );

  const searchableItems = useMemo(() => {
    if (!isAlbumMode && !isSongMode) return listItems;

    // Deduplicate listItems, excluding items already placed in tiers
    const mergedItems = new Map<number, AlbumItem>();

    listItems.forEach((item) => {
      if (!mergedItems.has(item.Id) && !renderedItemIds.has(item.Id)) {
        mergedItems.set(item.Id, item);
      }
    });

    return Array.from(mergedItems.values());
  }, [isAlbumMode, isSongMode, listItems, renderedItemIds]);

  const searchSourceItems = useMemo(() => {
    if (!isAlbumMode && !isSongMode) return searchableItems;

    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return searchableItems;

    const mergedItems = new Map<number, AlbumItem>();

    // The server only searches the site catalogue, so carry the user's own custom
    // items across and let the local filter below match them on the search term.
    searchableItems.forEach((item) => {
      if (item.IsCustom && !renderedItemIds.has(item.Id)) {
        mergedItems.set(item.Id, item);
      }
    });

    searchResults.forEach((item) => {
      if (!mergedItems.has(item.Id) && !renderedItemIds.has(item.Id)) {
        mergedItems.set(item.Id, item);
      }
    });

    return Array.from(mergedItems.values());
  }, [isAlbumMode, isSongMode, renderedItemIds, searchResults, searchableItems, searchTerm]);

  const albumsByYear = useMemo<Record<string, AlbumItem[]>>(
    () =>
      albums.reduce<Record<string, AlbumItem[]>>((acc, album) => {
        const year = album.YearReleased ? album.YearReleased.toString() : unknownYearLabel;
        if (!acc[year]) acc[year] = [];
        acc[year].push(album);
        return acc;
      }, {}),
    [albums, unknownYearLabel]
  );

  const artistsByLetter = useMemo<Record<string, AlbumItem[]>>(
    () =>
      artists.reduce<Record<string, AlbumItem[]>>((acc, artist) => {
        const letter = (artist.Artist || unknownArtistLabel).charAt(0).toUpperCase();
        if (!acc[letter]) acc[letter] = [];
        acc[letter].push(artist);
        return acc;
      }, {}),
    [artists, unknownArtistLabel]
  );

  const groupCounts = useMemo<Record<string, number>>(() => {
    if (isYearMode) return Object.fromEntries(Object.entries(albumsByYear).map(([year, list]) => [year, list.length]));
    if (isArtistMode)
      return Object.fromEntries(
        Object.entries(artistsByLetter).map(([letter, list]) => [letter, list.length])
      );
    return {};
  }, [albumsByYear, artistsByLetter, isArtistMode, isYearMode]);

  const groupOptions = useMemo(() => {
    if (isYearMode) {
      const years = availableYears.length > 0 ? availableYears : Object.keys(albumsByYear);
      return sortYears([...new Set([defaultYear, ...years])]);
    }
    if (isArtistMode) return ["All", ...Object.keys(artistsByLetter).sort((a, b) => a.localeCompare(b))];
    return [];
  }, [albumsByYear, availableYears, artistsByLetter, isArtistMode, isYearMode, defaultYear]);

  const filteredItems = useMemo(() => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return searchSourceItems.filter((item) => {
      // "Show custom added" isolates the user's own items and ignores the
      // letter / year grouping, which custom items rarely line up with.
      if (showCustomOnly && !item.IsCustom) return false;

      if (isArtistMode) {
        const artistName = item.Artist || unknownArtistLabel;
        const matchesGroup = showCustomOnly || selectedGroup === "All" || artistName.charAt(0).toUpperCase() === selectedGroup;
        if (!matchesGroup) return false;
        if (!lowercasedSearchTerm) return true;
        return artistName.toLowerCase().includes(lowercasedSearchTerm);
      }

      if (isSongMode) {
        const matchesSearch =
          (item.SongName?.toLowerCase().includes(lowercasedSearchTerm) ?? false) ||
          (item.Artist?.toLowerCase().includes(lowercasedSearchTerm) ?? false) ||
          (item.AlbumName?.toLowerCase().includes(lowercasedSearchTerm) ?? false);
        return matchesSearch;
      }

      const matchesSearch =
        (item.AlbumName?.toLowerCase().includes(lowercasedSearchTerm) ?? false) ||
        (item.Artist?.toLowerCase().includes(lowercasedSearchTerm) ?? false);
      if (!matchesSearch) return false;

      if (isYearMode && !showCustomOnly) {
        const year = item.YearReleased ? item.YearReleased.toString() : unknownYearLabel;
        return year === selectedGroup;
      }

      return true;
    });
  }, [
    isArtistMode,
    isSongMode,
    isYearMode,
    searchTerm,
    searchSourceItems,
    selectedGroup,
    showCustomOnly,
    unknownArtistLabel,
    unknownYearLabel,
  ]);

  // Debounced artist autocomplete search
  const debouncedArtistSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (!term.trim()) {
          setArtistSuggestions([]);
          setLoadingSuggestions(false);
          return;
        }
        try {
          const encodedTerm = encodeURIComponent(term.trim());
          const result = await props.getCommands.getCommon(`searchArtists?query=${encodedTerm}`);
          if (Array.isArray(result)) {
            setArtistSuggestions(result.map((a: any) => a.Artist));
          }
        } catch {
          setArtistSuggestions([]);
        } finally {
          setLoadingSuggestions(false);
        }
      }, 300),
    [props.getCommands]
  );

  useEffect(() => {
    return () => {
      debouncedArtistSearch.cancel();
    };
  }, [debouncedArtistSearch]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string, songMode: boolean) => {
        if (!term.trim()) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }
        try {
          const encodedTerm = encodeURIComponent(term.trim());
          const endpoint = songMode
            ? `searchSongs?query=${encodedTerm}`
            : `searchAlbums?query=${encodedTerm}`;
          const result = await props.getCommands.getCommon(endpoint);
          if (Array.isArray(result)) {
            setSearchResults(result);
          }
        } catch (error) {
          console.error("Failed to fetch search results", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [props.getCommands]
  );

  useEffect(() => {
    if (!isAlbumMode && !isSongMode) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debouncedSearch(searchTerm, isSongMode);

    return () => {
      debouncedSearch.cancel();
    };
  }, [isAlbumMode, isSongMode, debouncedSearch, searchTerm]);

  const resetVisibleCount = useCallback(() => {
    setVisibleCount(Math.min(48, filteredItems.length));
  }, [filteredItems.length]);

  useEffect(() => {
    if (initialGroupByType === listType && initialGroupByValue) {
      setSelectedGroup(initialGroupByValue);
      if ((isAlbumMode || isSongMode) && initialGroupByValue !== "All") {
        setArtistSearchInput(initialGroupByValue);
      }
      return;
    }
    setSelectedGroup(listType === "Year" ? defaultYear : "All");
  }, [defaultYear, initialGroupByType, initialGroupByValue, listType, isAlbumMode, isSongMode]);

  useEffect(() => {
    resetVisibleCount();
  }, [resetVisibleCount]);

  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 120) {
        setVisibleCount((prev) => {
          const next = Math.min(prev + 32, filteredItems.length);
          // If we've shown almost all loaded items and server has more, fetch next page
          if (next >= filteredItems.length - 16 && props.pagination?.hasMore && props.onLoadMore) {
            props.onLoadMore();
          }
          return next;
        });
      }
    },
    [filteredItems.length, props.pagination?.hasMore, props.onLoadMore]
  );

  useEffect(() => {
    if (typeof props.updateUrlParams === "function") {
      props.updateUrlParams(listType, selectedGroup);
    }
  }, [listType, props.updateUrlParams, selectedGroup]);

  const visibleItems = useMemo(() => filteredItems.slice(0, visibleCount), [filteredItems, visibleCount]);

  const customType = listTypeToCustomType(listType);

  const openCustomPopup = useCallback(
    (prefillName = "") => {
      // Custom items belong to an account, so a guest has to sign in first.
      if (!props.state?.isAuthenticated) {
        props.basicFunctions?.updateState({ showSignInPrompt: true });
        return;
      }
      setCustomPopupName(prefillName);
      setCustomPopupOpen(true);
    },
    [props.state?.isAuthenticated, props.basicFunctions]
  );

  const handleArtistSelect = useCallback((artistName: string | null) => {
    const val = artistName || "All";
    setSelectedGroup(val);

    // Trigger server-side filter
    if (props.onArtistFilterChange) {
      props.onArtistFilterChange(val === "All" ? null : val);
    }

    // Update title
    if (props.basicFunctions?.updateState) {
      props.basicFunctions.updateState({
        tierListName: val !== "All" ? val : "",
      });
    }
  }, [props.onArtistFilterChange, props.basicFunctions]);

  const selectSx = {
    backgroundColor: "var(--hover-overlay)",
    borderRadius: "8px",
    color: "var(--text-primary)",
    "& .MuiSelect-select": {
      color: "var(--text-primary)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--border-mid)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--border-strong)",
    },
    "& .MuiSelect-icon": {
      color: "var(--text-faint)",
    },
  };

  const autocompleteSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "var(--hover-overlay)",
      borderRadius: "8px",
      color: "var(--text-primary)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--border-mid)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--border-strong)",
      },
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: "var(--text-faint)",
    },
    "& .MuiAutocomplete-popupIndicator": {
      color: "var(--text-faint)",
    },
  };

  const renderGroupSelect = (): JSX.Element | null => {
    const containerSx = {
      top: 0,
      width: "240px",
      display: "flex",
      flexDirection: "column",
      height: "max-content",
      gap: "6px",
      marginRight: "16px",
      paddingTop: "12px",
    };

    const labelEl = (
      <Typography variant="caption" sx={{ color: "var(--text-faint)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {t("tierList.groupBy")}
      </Typography>
    );

    // Artists mode (A-Z) and Year mode: keep existing Select dropdown
    if (isArtistMode || isYearMode) {
      if (!groupOptions.length) return null;
      return (
        <Box sx={containerSx}>
          {labelEl}
          <Select
            value={selectedGroup}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedGroup(val);
              if (isYearMode && props.onYearChange) {
                props.onYearChange(val);
              }
              if (props.basicFunctions?.updateState) {
                let title = "";
                if (val && val !== "All") {
                  if (isArtistMode && val.length === 1) {
                    title = t("tierList.artistsTitle", { letter: val });
                  } else if (isYearMode) {
                    title = t("tierList.bestAlbumsOf", { value: val });
                  } else {
                    title = val;
                  }
                }
                props.basicFunctions.updateState({ tierListName: title });
              }
            }}
            size="small"
            fullWidth
            sx={selectSx}
          >
            {groupOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === "All"
                  ? t("tierList.allGroups")
                  : isYearMode
                    ? `${option} (${yearCounts[option] || groupCounts[option] || 0})`
                    : `${option} (${groupCounts[option] || 0})`}
              </MenuItem>
            ))}
          </Select>
        </Box>
      );
    }

    // Albums and Songs mode: use Autocomplete search
    if (isAlbumMode || isSongMode) {
      return (
        <Box sx={containerSx}>
          {labelEl}
          <Autocomplete
            options={artistSuggestions}
            value={selectedGroup === "All" ? null : selectedGroup}
            inputValue={artistSearchInput}
            onInputChange={(_, newValue, reason) => {
              setArtistSearchInput(newValue);
              if (reason === "input") {
                setLoadingSuggestions(true);
                debouncedArtistSearch(newValue);
              }
            }}
            onChange={(_, newValue) => {
              handleArtistSelect(newValue);
            }}
            loading={loadingSuggestions}
            noOptionsText={artistSearchInput.trim() ? t("tierList.noArtistResults") : t("tierList.searchArtistPlaceholder")}
            size="small"
            fullWidth
            clearOnEscape
            sx={autocompleteSx}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: "var(--surface-1)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-mid)",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("tierList.searchArtistPlaceholder")}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSuggestions ? <CircularProgress size={18} sx={{ color: "var(--text-faint)" }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      ref={provided.innerRef}
      {...provided.droppableProps}
      id="albumLayoutBox"
      sx={{
        width: "600px",
        height: "fit-content",
        padding: 2.5,
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--surface-1)",
        border: "1px solid var(--border-soft)",
      }}
    >
      {renderGroupSelect()}

      <Box sx={{ flex: 1, display: "flex", height: "800px", flexDirection: "column" }}>
        <Box sx={{ top: 0, zIndex: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            placeholder={t("tierList.searchPlaceholder")}
            variant="outlined"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              mt: 1,
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "var(--hover-overlay)",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--border-strong)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--border-mid)",
              },
            }}
            slotProps={{
              input: {
                endAdornment: isSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {props.onShuffle && (
            <Tooltip title={t("tierList.shuffle", { defaultValue: "Shuffle" })}>
              <IconButton
                onClick={props.onShuffle}
                sx={{
                  color: "var(--text-faint)",
                  mb: 1,
                  "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--hover-overlay)" },
                }}
              >
                <ShuffleIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("customItems.addTooltip")}>
            <IconButton
              id="addCustomItemButton"
              onClick={() => openCustomPopup(searchTerm.trim())}
              sx={{
                color: "var(--text-faint)",
                mb: 1,
                "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--hover-overlay)" },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showCustomOnly}
                onChange={(e) => setShowCustomOnly(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "var(--text-secondary)" }}>
                {t("customItems.showCustomOnly")}
              </Typography>
            }
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            paddingBottom: "16px",
            maxHeight: "800px",
          }}
          onScroll={handleScroll}
        >
          <Box
            id="selectedGroupBox"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(100px, 1fr))",
              gap: "8px",
              maxHeight: "800px",
            }}
          >
            {visibleItems.map((item, index) => {
              const cover = covers[coverType][item.Id];
              return (
                <PictureCard
                  key={item.Id}
                  keyItem={item.Id}
                  pictureItem={item}
                  cover={cover}
                  index={index}
                  draggableId={item.Id.toString()}
                  provided={provided}
                  onSongPreview={handleSongPreview}
                />
              );
            })}
          </Box>
          {!visibleItems.length && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 2 }}>
              <Typography variant="subtitle2" color="var(--text-primary)" textAlign="center">
                {showCustomOnly ? t("customItems.noCustomItems") : t("tierList.noResults")}
              </Typography>
              <Typography variant="caption" color="var(--text-faint)" textAlign="center">
                {t("customItems.notOnSite")}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openCustomPopup(searchTerm.trim())}
                sx={{
                  textTransform: "none",
                  borderColor: "var(--border-mid)",
                  color: "var(--text-secondary)",
                  "&:hover": {
                    borderColor: "var(--border-strong)",
                    backgroundColor: "var(--hover-overlay)",
                  },
                }}
              >
                {t("customItems.addCta")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {provided.placeholder}

      <AlbumSongsPopup
        open={songPopup.open}
        onClose={() => setSongPopup((prev) => ({ ...prev, open: false }))}
        albumId={songPopup.albumId}
        albumName={songPopup.albumName}
        artistName={songPopup.artistName}
        albumCover={songPopup.albumCover}
        getCommands={props.getCommands}
      />

      <CustomItemPopup
        open={customPopupOpen}
        onClose={() => setCustomPopupOpen(false)}
        defaultType={customType}
        defaultName={customPopupName}
        state={props.state}
        basicFunctions={props.basicFunctions}
        createCommands={props.createCommands}
        postCommands={props.postCommands}
        mainFunctions={props.mainFunctions}
      />
    </Box>
  );
};

export default AlbumLayout;
